import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Users, TrendingUp, BookOpen, Shield, BarChart3 } from 'lucide-react';

const features = [
  {
    icon: Clock,
    title: 'Attendance Tracking',
    description: 'Monitor attendance in real time.',
  },
  {
    icon: BookOpen,
    title: 'Leave Management',
    description: 'Request & approve leave instantly.',
  },
  {
    icon: Shield,
    title: 'Payroll',
    description: 'Secure payroll & salary management.',
  },
  {
    icon: Users,
    title: 'Employee Directory',
    description: 'Connect with your organization.',
  },
  {
    icon: TrendingUp,
    title: 'Performance',
    description: 'Track team goals & appraisals.',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Insights',
    description: 'Real-time workforce reporting.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: 'easeOut' },
  },
};

// Inline SVG illustration — HR Dashboard / Workspace scene (Luminous Blue theme overlay)
const HRIllustration = () => (
  <svg viewBox="0 0 400 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[310px] mx-auto drop-shadow-xl overflow-visible">
    <defs>
      <clipPath id="screenClip">
        <rect x="84" y="36" width="232" height="140" rx="6" />
      </clipPath>
      <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.0" />
      </linearGradient>
    </defs>

    {/* Main monitor outer body */}
    <motion.rect 
      initial={{ opacity: 0, y: 15, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      x="78" y="28" width="244" height="156" rx="10" fill="#FFFFFF" stroke="#CBD5E1" strokeWidth="2.5"
    />

    {/* Monitor Stand */}
    <motion.rect 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
      x="175" y="184" width="50" height="14" rx="2" fill="#E2E8F0"
    />
    <motion.rect 
      initial={{ opacity: 0, scaleX: 0 }}
      animate={{ opacity: 1, scaleX: 1 }}
      transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
      x="150" y="198" width="100" height="6" rx="3" fill="#94A3B8"
    />

    {/* Monitor Screen Content */}
    <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.6 }}>
      {/* Screen background */}
      <rect x="84" y="36" width="232" height="140" rx="6" fill="#F8FAFC" />

      <g clipPath="url(#screenClip)">
        {/* Top Navbar */}
        <rect x="84" y="36" width="232" height="14" fill="#FFFFFF" />
        <line x1="84" y1="50" x2="316" y2="50" stroke="#F1F5F9" strokeWidth="1" />
        <circle cx="93" cy="43" r="3" fill="#2563EB" />
        <rect x="100" y="41.5" width="28" height="3" rx="1.5" fill="#CBD5E1" />
        
        <circle cx="295" cy="43" r="3" fill="#E2E8F0" />
        <rect x="300" y="41.5" width="10" height="3" rx="1.5" fill="#E2E8F0" />

        {/* 5 Top KPI Cards */}
        <rect x="88" y="54" width="41" height="18" rx="3" fill="#EFF6FF" stroke="#DBEAFE" strokeWidth="0.5" />
        <circle cx="95" cy="63" r="4" fill="#3B82F6" fillOpacity="0.8" />
        <rect x="102" y="59" width="18" height="3" rx="1" fill="#93C5FD" />
        <rect x="102" y="64" width="12" height="2.5" rx="1" fill="#BFDBFE" />

        <rect x="133" y="54" width="41" height="18" rx="3" fill="#FFF7ED" stroke="#FFEDD5" strokeWidth="0.5" />
        <circle cx="140" cy="63" r="4" fill="#F97316" fillOpacity="0.8" />
        <rect x="147" y="59" width="18" height="3" rx="1" fill="#FDBA74" />
        <rect x="147" y="64" width="12" height="2.5" rx="1" fill="#FED7AA" />

        <rect x="178" y="54" width="41" height="18" rx="3" fill="#FEF3C7" stroke="#FDE68A" strokeWidth="0.5" />
        <rect x="182" y="59" width="8" height="8" rx="2" fill="#F59E0B" fillOpacity="0.8" />
        <rect x="193" y="59" width="18" height="3" rx="1" fill="#FCD34D" />
        <rect x="193" y="64" width="12" height="2.5" rx="1" fill="#FDE68A" />

        <rect x="223" y="54" width="41" height="18" rx="3" fill="#EFF6FF" stroke="#DBEAFE" strokeWidth="0.5" />
        <circle cx="230" cy="63" r="4" fill="#2563EB" fillOpacity="0.8" />
        <rect x="237" y="59" width="18" height="3" rx="1" fill="#93C5FD" />
        <rect x="237" y="64" width="12" height="2.5" rx="1" fill="#BFDBFE" />

        <rect x="268" y="44" width="44" height="18" rx="3" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="0.5" />
        <circle cx="290" cy="53" r="4" fill="#64748B" fillOpacity="0.3" />

        {/* Middle Left: Attendance Overview */}
        <g>
          <rect x="88" y="76" width="108" height="47" rx="4" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="0.6" />
          <text x="93" y="84" fontSize="4.2" fontWeight="700" fill="#1E293B" fontFamily="sans-serif">Attendance Overview</text>
          
          <rect x="154" y="80" width="38" height="8" rx="2" fill="#ECFDF5" />
          <circle cx="158" cy="84" r="1.5" fill="#10B981" />
          <text x="161" y="85.5" fontSize="3" fontWeight="700" fill="#047857" fontFamily="sans-serif">98% Present</text>

          <path d="M92 114 Q103 106 113 108 T133 98 T153 106 T173 94 T192 95 L192 120 L92 120 Z" fill="url(#lineGrad)" />
          <path d="M92 114 Q103 106 113 108 T133 98 T153 106 T173 94 T192 95" fill="none" stroke="#3B82F6" strokeWidth="1.2" strokeLinecap="round" />
        </g>

        {/* Middle Right: Employee Summary */}
        <g>
          <rect x="200" y="76" width="112" height="47" rx="4" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="0.6" />
          <text x="205" y="84" fontSize="4.2" fontWeight="700" fill="#1E293B" fontFamily="sans-serif">Employee Summary</text>
          
          <circle cx="230" cy="101" r="11" fill="none" stroke="#E2E8F0" strokeWidth="4" />
          <circle cx="230" cy="101" r="11" fill="none" stroke="#2563EB" strokeWidth="4" strokeDasharray="45 70" strokeDashoffset="0" />
          <circle cx="230" cy="101" r="11" fill="none" stroke="#10B981" strokeWidth="4" strokeDasharray="18 70" strokeDashoffset="-45" />
          <circle cx="230" cy="101" r="11" fill="none" stroke="#F97316" strokeWidth="4" strokeDasharray="8 70" strokeDashoffset="-63" />
          <text x="230" y="101.5" textAnchor="middle" fontSize="3.5" fontWeight="700" fill="#0F172A" fontFamily="sans-serif">245</text>
          <text x="230" y="104.5" textAnchor="middle" fontSize="2" fill="#64748B" fontFamily="sans-serif">Total</text>

          <circle cx="254" cy="95" r="1.5" fill="#2563EB" />
          <text x="258" y="96" fontSize="2.8" fontWeight="600" fill="#334155" fontFamily="sans-serif">Active</text>
          
          <circle cx="254" cy="101" r="1.5" fill="#10B981" />
          <text x="258" y="102" fontSize="2.8" fontWeight="600" fill="#334155" fontFamily="sans-serif">On Leave</text>

          <circle cx="254" cy="107" r="1.5" fill="#F97316" />
          <text x="258" y="108" fontSize="2.8" fontWeight="600" fill="#334155" fontFamily="sans-serif">Inactive</text>
        </g>

        {/* Bottom Left: Leave Requests */}
        <g>
          <rect x="88" y="127" width="108" height="46" rx="4" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="0.6" />
          <text x="93" y="135" fontSize="4.2" fontWeight="700" fill="#1E293B" fontFamily="sans-serif">Leave Requests</text>

          <text x="93" y="143" fontSize="3" fontWeight="600" fill="#334155" fontFamily="sans-serif">Annual Leave</text>
          <rect x="168" y="139.5" width="24" height="4.5" rx="1.5" fill="#EFF6FF" />
          <text x="180" y="143" textAnchor="middle" fontSize="2.5" fontWeight="600" fill="#2563EB" fontFamily="sans-serif">Pending</text>

          <text x="93" y="152" fontSize="3" fontWeight="600" fill="#334155" fontFamily="sans-serif">Sick Leave</text>
          <rect x="168" y="148.5" width="24" height="4.5" rx="1.5" fill="#ECFDF5" />
          <text x="180" y="152" textAnchor="middle" fontSize="2.5" fontWeight="600" fill="#059669" fontFamily="sans-serif">Approved</text>
        </g>

        {/* Bottom Right: Payroll Overview */}
        <g>
          <rect x="200" y="127" width="112" height="46" rx="4" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="0.6" />
          <text x="205" y="135" fontSize="4.2" fontWeight="700" fill="#1E293B" fontFamily="sans-serif">Payroll Overview</text>
          <text x="205" y="144" fontSize="4.8" fontWeight="800" fill="#0F172A" fontFamily="sans-serif">₹ 24,85,000</text>
          <text x="205" y="149" fontSize="2.5" fill="#64748B" fontFamily="sans-serif">Total Payroll</text>

          {[
            { x: 260, h: 8, y: 159 },
            { x: 267, h: 14, y: 153 },
            { x: 274, h: 10, y: 157 },
            { x: 281, h: 18, y: 149 },
            { x: 288, h: 12, y: 155 },
            { x: 295, h: 22, y: 145 },
            { x: 302, h: 16, y: 151 },
          ].map((b, i) => (
            <rect key={i} x={b.x} y={b.y} width="4.5" height={b.h} rx="1" fill="#3B82F6" fillOpacity={i === 5 ? "1" : "0.7"} />
          ))}
        </g>
      </g>
    </motion.g>

    {/* Floating Badges Surrounding Monitor */}
    
    {/* 1. Top Left Floating Pulse Sphere */}
    <motion.g animate={{ y: [-5, 5, -5], scale: [1, 1.1, 1] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
      <circle cx="55" cy="50" r="10" fill="#FFFFFF" fillOpacity="0.9" stroke="#E2E8F0" strokeWidth="1"/>
      <circle cx="55" cy="50" r="5" fill="#3B82F6"/>
    </motion.g>

    {/* 2. Top Right Floating Shield Badge */}
    <motion.g animate={{ y: [5, -5, 5] }} transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}>
      <rect x="330" y="42" width="36" height="28" rx="8" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1.5"/>
      <path d="M348 50 C343 51 343 55 348 61 C353 55 353 51 348 50 Z" fill="#6366F1"/>
    </motion.g>

    {/* 3. Middle Left Floating Notification Bell Badge */}
    <motion.g animate={{ y: [-6, 6, -6] }} transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut", delay: 1 }}>
      <rect x="28" y="105" width="38" height="28" rx="8" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1.5"/>
      <circle cx="47" cy="118" r="4.5" fill="#0EA5E9"/>
      <circle cx="53" cy="113" r="2.5" fill="#EF4444"/>
    </motion.g>

    {/* 4. Bottom Left Floating User Profile Avatar */}
    <motion.g animate={{ y: [5, -5, 5] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}>
      <circle cx="55" cy="175" r="15" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1.5"/>
      <circle cx="55" cy="170" r="6" fill="#2563EB"/>
      <path d="M43 186 C43 179 67 179 67 186" fill="#93C5FD"/>
      <circle cx="65" cy="180" r="3" fill="#10B981" stroke="#FFFFFF" strokeWidth="1"/>
    </motion.g>

    {/* 5. Bottom Right Floating Checkmark Badge */}
    <motion.g animate={{ y: [-5, 5, -5] }} transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}>
      <rect x="330" y="170" width="40" height="26" rx="8" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1.5"/>
      <path d="M342 183 L348 189 L358 179" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </motion.g>
  </svg>
);

const FeatureShowcase = () => {
  return (
    <div className="relative h-full bg-gradient-to-br from-[#EBF3FE] via-[#D8E6FE] to-[#C2DCFF] border-l border-blue-200/60 rounded-r-3xl overflow-hidden flex flex-col justify-center p-7 lg:p-9 shadow-inner">
      {/* Background decorative soft glow elements */}
      <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-72 h-72 rounded-full bg-indigo-400/15 blur-3xl pointer-events-none" />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.25] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #2563EB 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Content Container (Centered natural layout without vertical stretch gaps) */}
      <div className="relative z-10 flex flex-col justify-center h-full my-auto space-y-4">
        {/* Hero Section */}
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-2 flex justify-center"
          >
            <HRIllustration />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl lg:text-2xl font-extrabold text-slate-900 font-sans tracking-tight">
              Empowering Your Workforce
            </h2>
            <p className="text-slate-600 text-xs font-sans mt-1 max-w-sm mx-auto font-medium leading-relaxed">
              Everything you need to manage your team in real time.
            </p>
          </motion.div>
        </div>

        {/* Feature Cards Grid (Perfect 2x3 Centric Grid Layout - 6 Equal Cards) */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-2 gap-2.5 pt-4"
        >
          {features.map((feature, idx) => {
            const Icon = feature.icon;

            const badgeStyles = [
              'bg-blue-100/80 text-blue-700 border-blue-200',
              'bg-emerald-100/80 text-emerald-700 border-emerald-200',
              'bg-amber-100/80 text-amber-700 border-amber-200',
              'bg-purple-100/80 text-purple-700 border-purple-200',
              'bg-sky-100/80 text-sky-700 border-sky-200',
              'bg-indigo-100/80 text-indigo-700 border-indigo-200'
            ];

            return (
              <motion.div
                key={feature.title}
                variants={itemVariants}
                className="p-3 rounded-2xl bg-white/90 backdrop-blur-md border border-blue-200/60 hover:bg-white hover:border-blue-400 hover:shadow-md transition-all duration-300 flex items-start gap-2.5 shadow-2xs group"
              >
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 border ${badgeStyles[idx % badgeStyles.length]}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-slate-900 font-bold text-xs font-sans leading-tight truncate">
                    {feature.title}
                  </h4>
                  <p className="text-slate-600 text-[10px] font-sans leading-snug mt-0.5 line-clamp-2">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};

export default FeatureShowcase;
